import { AnyAction } from 'redux';

type TaskId = string | number;
type MotionTask = {id: TaskId; [key: string]: any};
type Duplicate = {sourceId: TaskId; task: MotionTask};
type MotionState = {duplicates: Record<string, Duplicate>};
const initialState: MotionState = {duplicates: {}};
export const TASK_DUPLICATED = 'tasks/duplicated';
const DUPLICATE_SEEN = 'tasks/duplicateSeen';
export const taskDuplicated = (sourceId: TaskId, task: MotionTask) => ({type: TASK_DUPLICATED, payload: {sourceId, task}});
export const taskDuplicateSeen = (id: TaskId) => ({type: DUPLICATE_SEEN, payload: String(id)});

export function taskMotionReducer(state = initialState, action: AnyAction): MotionState {
    if (action.type === 'FETCH_DATA_USER_ACCOUNT_LOGOUT') return initialState;
    if (['REMOVE_TODO', 'REMOVE_TODO_INCOMING', 'REMOVE_TODO_ARCHIVE', 'REMOVE_TODO_CALENDAR'].includes(action.type)) {
        const duplicates = {...state.duplicates};
        delete duplicates[String(action.payload)];
        return {duplicates};
    }
    if (action.type === TASK_DUPLICATED) return {duplicates: {...state.duplicates, [String(action.payload.task.id)]: action.payload}};
    if (action.type === DUPLICATE_SEEN) {
        const duplicates = {...state.duplicates};
        delete duplicates[action.payload];
        return {duplicates};
    }
    return state;
}

export function insertTaskDuplicate<T extends {id: TaskId}>(tasks: T[], sourceId: TaskId, task: T): T[] {
    if (String(sourceId) === String(task.id)) return tasks;
    const source = tasks.find(item => String(item.id) === String(sourceId));
    if (!source) return tasks;
    const existing = tasks.find(item => String(item.id) === String(task.id));
    const remaining = tasks.filter(item => String(item.id) !== String(task.id));
    const index = remaining.findIndex(item => String(item.id) === String(sourceId));
    const copy = {...(existing ?? task), ...('isBacklog' in source ? {isBacklog: (source as any).isBacklog} : {})};
    return [...remaining.slice(0, index + 1), copy, ...remaining.slice(index + 1)];
}

// A copy of a visible source can land just beyond the current server page.
// Keep that confirmed copy beside its source until it is acknowledged. A new
// filter which excludes the source does not retain an unrelated copy.
export function preserveTaskDuplicates<T extends {id: TaskId}>(tasks: T[], state: unknown): T[] {
    const duplicates = (state as {taskMotion?: MotionState})?.taskMotion?.duplicates ?? {};
    return Object.values(duplicates).reduce((result, copy) =>
        insertTaskDuplicate(result, copy.sourceId, copy.task as T), tasks);
}
